import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { PlusOutlined } from '@ant-design/icons'
import { PageHeader, Button } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { procedureConstants } from 'locale/procedures'
import useRoleAccess from '../../../../components/AccessControl/useRoleAccess'
import { UserRole } from '@lmc/ts-common'
import { ProcedureList } from './components/ProcedureList'
import ProcedureModal from './components/ProcedureModal'
import { useProceduresList } from './hooks/useProceduresList'
import { Procedure } from './types'

export const ResidentProceduresScreen: React.FC = () => {
  const { t } = useTranslation(NAMESPACE.PROCEDURES)
  const { id: residentId } = useParams<{ id: string }>()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedProcedure, setSelectedProcedure] = useState<
    Procedure | undefined
  >(undefined)

  const {
    procedures,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useProceduresList({
    residentId: residentId || '',
    limit: 50
  })

  const hasAddAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin, UserRole.seniorCarer],
    [
      {
        i: 'create',
        this: 'Procedure'
      }
    ]
  )

  const handleAddClick = () => {
    setSelectedProcedure(undefined)
    setIsModalVisible(true)
  }

  const handleEdit = (procedure: Procedure) => {
    setSelectedProcedure(procedure)
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedProcedure(undefined)
  }

  const handleSuccess = () => {
    refetch()
  }

  return (
    <>
      <PageHeader title={t(procedureConstants.PROCEDURES)} sticky>
        {hasAddAccess && (
          <Button variant="primary" onClick={handleAddClick}>
            <PlusOutlined />
            {t(procedureConstants.ADD_PROCEDURE)}
          </Button>
        )}
      </PageHeader>

      <ProcedureList
        procedures={procedures}
        isLoading={isLoading}
        onEdit={handleEdit}
        onRefetch={refetch}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />

      <ProcedureModal
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        procedure={selectedProcedure}
        residentId={residentId || ''}
      />
    </>
  )
}
